import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { MessageTypeEnum, type Chat, type ChatMessage } from 'api-client';
import { useAppContext } from '@/context';
import { profileFailure } from '@/users/components/auth/profileRequests';
import ChatTranscript from './ChatTranscript';
import MessageComposer from './MessageComposer';

const wait = (signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', cancel);
      resolve();
    }, 1000);
    const cancel = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    if (signal.aborted) cancel();
    else signal.addEventListener('abort', cancel, { once: true });
  });

export default function ChatRoom() {
  const { chatId = '' } = useParams<{ chatId: string }>();
  return <ChatSession key={chatId} chatId={chatId} />;
}
function ChatSession({ chatId }: { chatId: string }) {
  const { apiClients, user } = useAppContext();
  const [chat, setChat] = useState<Chat | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const [thinking, setThinking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState<number>(0);
  const generation = useRef<number>(0);
  const abort = useRef<AbortController | null>(null);
  const merge = useCallback(
    (message: ChatMessage) =>
      setChat((value) =>
        value
          ? {
              ...value,
              messages: [...value.messages.filter((item) => item.id !== message.id), message],
            }
          : value,
      ),
    [],
  );
  const poll = useCallback(
    async (taskId: string, signal: AbortSignal, current: number) => {
      for (let attempt = 0; attempt < 60; attempt += 1) {
        const progress = await apiClients.chat.chatsResponses({ id: chatId, taskId }, { signal });
        if (current !== generation.current) return;
        if (progress.complete) {
          if (!progress.success || !progress.message)
            throw new Error(progress.error || 'The AI response could not be completed.');
          merge(progress.message);
          const refreshed = await apiClients.chat.chatsRetrieve({ id: chatId }, { signal });
          if (current === generation.current) setChat(refreshed);
          return;
        }
        await wait(signal);
      }
      throw new Error('The AI response is still pending. Retry loading the chat to check its status.');
    },
    [apiClients.chat, chatId, merge],
  );
  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    setError(null);
    void apiClients.chat
      .chatsRetrieve({ id: chatId }, { signal: controller.signal })
      .then(async (value) => {
        if (current !== generation.current) return;
        setChat(value);
        if (value.messages.some((item) => item.responseState === 'FAILURE'))
          setError('An AI response could not be completed. Your saved messages are retained.');
        setLoading(false);
        const tasks = value.messages.filter((item) => item.responseState === 'PENDING' && item.responseTaskId);
        if (tasks.length) {
          setThinking(true);
          for (const item of tasks) await poll(item.responseTaskId, controller.signal, current);
        }
      })
      .catch(async (failure: unknown) => {
        if (controller.signal.aborted || current !== generation.current) return;
        const message =
          failure instanceof Error && !(failure.name === 'ResponseError')
            ? failure.message
            : await profileFailure(failure, 'Could not load this chat. Please retry.');
        if (current === generation.current) setError(message);
      })
      .finally(() => {
        if (current === generation.current) {
          setLoading(false);
          setThinking(false);
        }
      });
    return () => {
      generation.current += 1;
      controller.abort();
    };
  }, [chatId, apiClients.chat, retry, poll]);
  const send = async () => {
    if (!chat || !draft.trim() || sending || thinking || !abort.current) return;
    const current = generation.current;
    const signal = abort.current.signal;
    setSending(true);
    setError(null);
    try {
      const posted = await apiClients.chat.chatsNewMessage(
        { id: chatId, chatMessageInput: { chat: chat.id, messageType: MessageTypeEnum.Human, content: draft.trim() } },
        { signal },
      );
      if (current !== generation.current) return;
      merge(posted);
      setDraft('');
      setSending(false);
      setThinking(true);
      await poll(posted.taskId, signal, current);
    } catch (failure) {
      if (signal.aborted || current !== generation.current) return;
      const message =
        failure instanceof Error && failure.name !== 'ResponseError'
          ? failure.message
          : await profileFailure(failure, 'Could not confirm this message. Reload the chat or retry.');
      if (current === generation.current) setError(message);
    } finally {
      if (current === generation.current) {
        setSending(false);
        setThinking(false);
      }
    }
  };
  return (
    <Stack spacing={3}>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={2} alignItems="center">
        <Button component={RouterLink} to="/chat/">
          Back to Chats
        </Button>
        <Typography variant="h4" component="h1">
          {chat?.name || 'Chat'}
        </Typography>
      </Stack>
      {loading && <CircularProgress aria-label="Loading chat" />}
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => setRetry((value) => value + 1)}>
              Reload
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {chat && (
        <>
          <ChatTranscript
            timeZone={user?.timezone || undefined}
            messages={chat.messages.map((item) => ({
              id: item.id,
              author:
                item.messageType === MessageTypeEnum.Human
                  ? 'You'
                  : item.messageType === MessageTypeEnum.Ai
                    ? 'Assistant'
                    : 'System',
              own: item.messageType === MessageTypeEnum.Human,
              content: item.content,
              timestamp: item.createdAt,
            }))}
          />
          {thinking && (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={20} aria-label="Waiting for AI response" />
              <Typography>Waiting for AI response…</Typography>
            </Stack>
          )}
          <MessageComposer
            value={draft}
            onChange={setDraft}
            onSend={() => {
              void send();
            }}
            pending={sending}
            unavailable={thinking || loading}
            helperText={thinking ? 'Wait for the current response before sending another message.' : undefined}
          />
        </>
      )}
    </Stack>
  );
}
